<template>
  <div class="cold-chain-monitor">

    <!-- Main Layout: 3-column grid -->
    <div class="main-layout" :class="{ 'left-collapsed': leftCollapsed }">
      <!-- Left Panel: Tree -->
      <aside class="left-panel">

        <leftContent ref='carrierContent' />
      </aside>

      <!-- Middle: Map -->
      <section class="map-section">
        <MiddleContent ref='map' />
      </section>

      <!-- Right Panel: Overview -->
      <aside class="right-panel">
        <rightOverview />
      </aside>
    </div>
  </div>
</template>

<script>
import leftContent from './components/leftTree.vue'
import MiddleContent from './components/middleMap.vue'
import rightOverview from './components/rightContent.vue'
import { mapTrackcarrierProbeState, mapTrackcarrierTraceState } from '@/api/modules/map'
import ArchDetail from '@/pages/dataBoard/mapMonitor/components/ArchDetail/ArchDetail'
import TraceDetail from '@/pages/dataBoard/mapMonitor/components/ArchDetail/TraceDetail'
import bus from '@/utils/bus'
import { _debounce } from '@/utils/common'

export default {
  components: {
    leftContent,
    MiddleContent,
    rightOverview,
    ArchDetail,
    TraceDetail
  },
  data () {
    return {
      drawerShow: false,
      info: null,
      leftCollapsed: false
    }
  },
  methods: {

  },
  mounted () {
    const getFixedInfo = _debounce(async row => {
      const res = await mapTrackcarrierProbeState({ id: row.id })
      this.drawerShow = true
      this.info = res.result
    }, 500)
    const getTraceInfo = _debounce(async row => {
      const res = await mapTrackcarrierTraceState({ id: row.id })
      this.drawerShow = true
      this.info = res.result
    }, 500)

    bus.$on('carrierListReady', async (carrierList, row, idx) => {
      bus.$emit('mapChangeCheck', idx)
      if (!row.isTrace) {
        await getFixedInfo(row)
      } else {
        await getTraceInfo(row)
      }
    })

    bus.$on('carrierListEmpty', (msg) => {
      this.$message.warning(msg)
      this.info = null
    })
  },
  beforeDestroy () {
    if (this.timeTimer) {
      clearInterval(this.timeTimer)
      this.timeTimer = null
    }
    bus.$off('carrierListReady')
    bus.$off('carrierListEmpty')
  }
}
</script>

<style lang='scss' scoped>
$bg-white: #ffffff;
$bg-gray-50: #f9fafb;
$bg-gray-100: #f3f4f6;
$bg-gray-200: #e5e7eb;
$border-color: #e5e7eb;
$border-light: #f3f4f6;
$text-900: #111827;
$text-700: #374151;
$text-500: #6b7280;
$text-400: #9ca3af;
$accent: #3b82f6;
$success: #10b981;
$warning: #f59e0b;
$danger: #ef4444;
$info: #60a5fa;

.cold-chain-monitor {
  width: 100%;
  height: 100%;
  display: flex;
  flex-direction: column;
  background: $bg-gray-50;
  color: $text-700;
  overflow: hidden;
}

// ==================== Topbar ====================
.topbar {
  height: 56px;
  min-height: 56px;
  background: $bg-white;
  border-bottom: 1px solid $border-color;
  display: flex;
  align-items: center;
  padding: 0 16px;
  gap: 16px;
  z-index: 100;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.05);
}

.topbar-left {
  display: flex;
  align-items: center;
  gap: 12px;
  min-width: 240px;
}

.topbar-btn {
  width: 36px;
  height: 36px;
  border: none;
  background: transparent;
  color: $text-500;
  border-radius: 6px;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 18px;
  transition: all 0.2s;

  &:hover {
    background: $bg-gray-100;
    color: $text-900;
  }
}

.sidebar-toggle {
  flex-shrink: 0;
}

.logo {
  display: flex;
  align-items: center;
  gap: 10px;
}

.logo-icon {
  width: 32px;
  height: 32px;
  background: linear-gradient(135deg, $accent, #1d4ed8);
  border-radius: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: 700;
  font-size: 13px;
  color: #fff;
  letter-spacing: 0.5px;
}

.logo-text {
  display: flex;
  flex-direction: column;
  line-height: 1.2;
}

.logo-text-main {
  font-size: 15px;
  font-weight: 600;
  color: $text-900;
}

.logo-text-sub {
  font-size: 10px;
  color: $text-400;
  letter-spacing: 0.5px;
}

.topbar-search {
  flex: 1;
  max-width: 420px;

  ::v-deep .el-input__inner {
    background: $bg-gray-50;
    border: 1px solid $border-color;
    color: $text-700;
    border-radius: 8px;

    &::placeholder {
      color: $text-400;
    }

    &:focus {
      border-color: $accent;
    }
  }

  ::v-deep .el-input__prefix {
    color: $text-400;
  }
}

.topbar-right {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-left: auto;
}

.topbar-clock {
  font-size: 13px;
  font-family: 'Consolas', 'Monaco', monospace;
  color: $text-500;
  margin-right: 8px;
  letter-spacing: 1px;
}

.topbar-badge {
  ::v-deep .el-badge__content.is-dot {
    background: $danger;
  }
}

// ==================== Main Layout ====================
.main-layout {
  flex: 1;
  display: grid;
  grid-template-columns: 320px 1fr 400px;
  grid-template-rows: 1fr;
  overflow: hidden;
  transition: grid-template-columns 0.3s ease;

  &.left-collapsed {
    grid-template-columns: 0px 1fr 400px;

    .left-panel {
      opacity: 0;
      pointer-events: none;
    }
  }
}

// ==================== Left Panel ====================
.left-panel {
  background: $bg-white;
  border-right: 1px solid $border-color;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  transition: opacity 0.3s ease;
}

.left-panel-header {
  height: 44px;
  min-height: 44px;
  display: flex;
  align-items: center;
  padding: 0 16px;
  border-bottom: 1px solid $border-color;
}

.left-panel-title {
  font-size: 13px;
  font-weight: 600;
  color: $text-500;
  text-transform: uppercase;
  letter-spacing: 1px;
}

.tree-container {
  flex: 1;
  overflow: auto;

  ::v-deep .content {
    width: 100% !important;
    height: 100% !important;
    border: none !important;
    background: transparent !important;
  }
}

// ==================== Map Section ====================
.map-section {
  position: relative;
  overflow: hidden;
  background: $bg-gray-100;
}

.map-overlay-top {
  position: absolute;
  top: 12px;
  left: 12px;
  z-index: 10;
  pointer-events: none;
}

.map-legend {
  display: flex;
  gap: 12px;
  background: rgba(255, 255, 255, 0.9);
  backdrop-filter: blur(8px);
  border: 1px solid $border-color;
  border-radius: 8px;
  padding: 8px 14px;
  pointer-events: auto;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
}

.legend-item {
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 12px;
  color: $text-500;
}

.legend-dot {
  width: 8px;
  height: 8px;
  border-radius: 50%;
  display: inline-block;

  &.dot-normal {
    background: $success;
    box-shadow: 0 0 6px rgba(16, 185, 129, 0.4);
  }

  &.dot-warning {
    background: $warning;
    box-shadow: 0 0 6px rgba(245, 158, 11, 0.4);
  }

  &.dot-danger {
    background: $danger;
    box-shadow: 0 0 6px rgba(239, 68, 68, 0.4);
  }

  &.dot-offline {
    background: $text-400;
  }
}

// ==================== Right Panel ====================
.right-panel {
  background: $bg-white;
  border-left: 1px solid $border-color;
  overflow: auto;
}

// ==================== Drawer ====================
::v-deep .el-drawer__wrapper {
  position: absolute;
  border-left: 1px solid $border-color;
}

::v-deep .v-modal {
  position: absolute;
}

::v-deep .el-drawer__header {
  margin-bottom: 0;
}

::v-deep .el-drawer__wrapper {
  width: 50%;
  left: 50%;

  .el-drawer__container {
    width: 100%;
  }

  .el-drawer.rtl {
    width: 100% !important;
  }
}

.drawer-flex-btn {
  position: absolute;
  right: 0;
  top: 50%;
  transform: translateY(-50%);
  z-index: 10;
  background: $bg-white;
  border: 1px solid $border-color;
  border-right: none;
  padding: 8px 4px;
  border-radius: 6px 0 0 6px;
  cursor: pointer;
  color: $text-500;
  transition: all 0.2s;
  box-shadow: -1px 0 4px rgba(0, 0, 0, 0.06);

  &:hover {
    background: $bg-gray-50;
    color: $text-900;
  }

  i {
    font-size: 14px;
  }
}
</style>
